import {CommonModule} from '@angular/common';
import {Component, EventEmitter, Input, Output} from '@angular/core';

/**
 * Like UiInput, but intended for DaisyUI `join` groups.
 *
 * Key differences vs <ui-input>:
 * - the input gets `join-item` so it can join with adjacent buttons
 * - the clear button stays INSIDE the input wrapper (absolute positioned)
 */
@Component({
  selector: 'ui-join-input',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './ui-join-input.component.html',
})
export class UiJoinInputComponent {
  @Input() type: 'text' | 'search' | 'email' | 'password' | 'number' = 'text';
  @Input() placeholder = '';
  @Input() value = '';
  @Input() disabled = false;
  @Input() size: 'sm' | 'md' | 'lg' = 'md';
  @Input() ariaLabel?: string;

  /**
   * When true, show a clear button as a join-item.
   */
  @Input() clearable = false;

  @Output() valueChange = new EventEmitter<string>();
  @Output() cleared = new EventEmitter<void>();

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.value = target.value;
    this.valueChange.emit(this.value);
  }

  onClear(): void {
    this.value = '';
    this.valueChange.emit(this.value);
    this.cleared.emit();
  }

  get computedWrapperClasses(): string {
    // Wrapper must be join-item so it merges with adjacent join items.
    // Also make it flexible so it can grow inside a flex join container.
    return 'relative inline-flex items-center join-item flex-1 min-w-0';
  }

  get computedInputClasses(): string {
    const classes = ['input', 'input-bordered', 'text-base', 'md:text-sm', 'w-full', 'pr-10'];

    if (this.size === 'sm') {
      classes.push('input-sm');
    } else if (this.size === 'lg') {
      classes.push('input-lg');
    }

    return classes.join(' ');
  }

  get computedClearBtnClasses(): string {
    const classes = ['absolute', 'right-2', 'top-1/2', '-translate-y-1/2', 'btn', 'btn-ghost', 'btn-circle'];

    if (this.size === 'sm') {
      classes.push('btn-sm');
    } else if (this.size === 'lg') {
      classes.push('btn-lg');
    } else {
      classes.push('btn-sm');
    }

    return classes.join(' ');
  }

  get showClearButton(): boolean {
    return this.clearable && !!this.value && !this.disabled;
  }
}

